import {Component, createResource, createMemo, For, Switch, Match, createEffect, on} from 'solid-js';
import { createAtom } from './createAtom';
import Calendar, { CalendarDate } from './Calendar';
import Event, { ProductEvent } from './Event';
import { distinct, formatDate, isDateEqual, thenWait } from '../Utils';

export interface Product {
    id: number;
    name: string;
    url: string;
    published: boolean;
    publishAt: string;
    expires: boolean;
    expiresAt: string;
    mediaName: string;
    mediaUrl: string;
    start: string;
    end: string;
    arenaId: number;
    arenaName: string;
    //arenaPosition: PositionDto;
    ingress: string;
    description: string;
    organizer: string;
    handicapFriendly: boolean;
    allergyAdapted: boolean;
    jubilee: boolean;
    artExhibition: boolean;
    wheelchairAccessible: boolean;
    schoolHoliday: boolean;
}

async function fetchEvents(period: string) {
    const res= await thenWait(
        2000,
        fetch(`/api/citybreak/events?period=${period}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
            },
        })
    );

    if (!res.ok) {
        throw new Error(`Error fetching data, got status ${res.status} ${res.statusText}`);
    }

    const products: Product[] | null = await res.json();
    
    // transform Products into Events
    return products?.map(p => {
        return {
            id: p.id,
            url: p.url,
            name: p.name,
            description: p.description,
            ingress: p.ingress,
            mediaUrl: p.mediaUrl,
            mediaName: p.mediaName,
            start: new Date(p.start),
            end: new Date(p.end),
        } as ProductEvent;
    }) ?? [];
}



export const CityBreakWidget: Component= () => {
    const now = new Date();
    const year = createAtom(now.getFullYear());
    const month = createAtom(1 + now.getMonth());
    const selectedDate = createAtom<number|null>(null);
    const [ events ] = createResource(() => {
        let currentMonth = month().toString();
        
        if (currentMonth.length == 1) {
            currentMonth = `0${currentMonth}`;
        }
        
        return `${year()}-${currentMonth}`;
    }, fetchEvents);

    const activeDates = createMemo(() => {
        const products = events();
        if (products == null) {
            return [];
        }

        return distinct(products.map(x => formatDate(x.start)));
    });
    
    const currentEvents = createMemo(() => {
       return events()?.filter(event => {
           return isDateEqual(event.start, new Date(year(), month() - 1, computeSelectedDate() ?? 1));
       }) ?? [];
    });

    const computeSelectedDate = createMemo(() => {
        if (events.loading) {
            return null;
        }
        
        const selected = selectedDate();
        const nowPeriod = formatDate(now);
        const selectedPeriod = formatDate(new Date(year(), month() - 1, 1));
        let activeDays = activeDates()
            .map(x => Number.parseInt(x.split('-')[2]))
            .sort((a, b) => a - b);

        if (selected == null) {
            if (now.getFullYear() == year() && (now.getMonth() == (month() - 1))) {
                activeDays = activeDays.filter(x => x >= now.getDate());
            }
            
            return activeDays[0];
        }

        return selected;
    });
    
    function setDate(date: CalendarDate) {
        year(date.year);
        month(date.month);
        selectedDate(null);
    }

    return (
        <div class="citybreak__widget">
            <Calendar year={year()}
                      month={month()}
                      activeDates={activeDates()}
                      selectedDate={computeSelectedDate()}
                      onSelect={selectedDate}
                      onNext={setDate}
                      onPrev={setDate}/>
    
            <Switch fallback={<p>Laddar...</p>}>
                <Match when={events.state == 'ready'}>
                    <ul class="citybreak__events">
                        <For each={currentEvents()}
                             fallback={<li>Finns inga händelser för valt datum</li>}>
                            {(product) => (
                                <Event product={product}/>
                            )}
                        </For>
                    </ul>
                </Match>
                <Match when={events.state == 'errored'}>
                    <p>Error fetching data!</p>
                </Match>
            </Switch>
        </div>
    );
}
